import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useChatsDeps } from './ChatsDeps'

/**
 * What the CRM knows about the fans on screen, fetched in batches once per
 * fan (the CRM list's hydrate* calls): OnlyMonster buying power, temperature
 * and the AI's overview (vibe, status). Buying power is skipped while the
 * person has it switched off in Styling.
 */
export interface FanExtras {
  buyingPower: Map<string, unknown>
  temperature: Map<string, number | null>
  ai: Map<string, { vibe: unknown; status: unknown }>
  /** Fans the AI overview has answered for (with or without a session). */
  aiAnswered: Set<string>
}

type Items = { items?: Record<string, unknown>[] }

interface Source {
  path: string
  chunk: number
  apply: (next: FanExtras, item: Record<string, unknown>) => void
}

const SOURCES: Record<'buyingPower' | 'temperature' | 'ai', Source> = {
  buyingPower: {
    path: '/v1/buying-power/batch',
    chunk: 100,
    apply: (n, it) => n.buyingPower.set(String(it.fan_id), it.buying_power),
  },
  temperature: {
    path: '/v1/fan-insights/batch',
    chunk: 100,
    apply: (n, it) => n.temperature.set(String(it.fan_id), it.temperature == null ? null : Number(it.temperature)),
  },
  ai: {
    path: '/v1/chats/ai-overview',
    chunk: 150,
    apply: (n, it) => n.ai.set(String(it.fan_id), { vibe: it.vibe, status: it.status }),
  },
}

const empty = (): FanExtras => ({ buyingPower: new Map(), temperature: new Map(), ai: new Map(), aiAnswered: new Set() })

export function useEnrichment(fanIds: string[], { buyingPower }: { buyingPower: boolean }): FanExtras & { refreshAi: (fanId: string) => void } {
  const { local } = useChatsDeps()
  const [extras, setExtras] = useState<FanExtras>(empty)
  const asked = useRef({ buyingPower: new Set<string>(), temperature: new Set<string>(), ai: new Set<string>() })
  // Answers for earlier pages must still land when the next page arrives, so
  // only unmounting (another account) discards them, not a new list of fans.
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  // Something changed for a fan (an AI hand-off): ask the AI overview again.
  const [again, setAgain] = useState(0)
  const refreshAi = useCallback((fanId: string) => {
    asked.current.ai.delete(fanId)
    setAgain((n) => n + 1)
  }, [])

  useEffect(() => {
    const ids = fanIds.filter((id) => /^[1-9]\d*$/.test(id))
    for (const name of Object.keys(SOURCES) as (keyof typeof SOURCES)[]) {
      if (name === 'buyingPower' && !buyingPower) continue
      const src = SOURCES[name]
      const seen = asked.current[name]
      const missing = ids.filter((id) => !seen.has(id))
      if (!missing.length) continue
      missing.forEach((id) => seen.add(id))
      for (let i = 0; i < missing.length; i += src.chunk) {
        const chunk = missing.slice(i, i + src.chunk)
        local<Items>(src.path, { fan_ids: chunk })
          .then((data) => {
            if (!mounted.current) return
            setExtras((prev) => {
              const next = {
                buyingPower: new Map(prev.buyingPower),
                temperature: new Map(prev.temperature),
                ai: new Map(prev.ai),
                aiAnswered: name === 'ai' ? new Set([...prev.aiAnswered, ...chunk]) : prev.aiAnswered,
              }
              for (const item of data?.items ?? []) if (item?.fan_id != null) src.apply(next, item)
              return next
            })
          })
          .catch(() => chunk.forEach((id) => seen.delete(id)))
      }
    }
  }, [fanIds, buyingPower, local, again])

  return useMemo(() => ({ ...extras, refreshAi }), [extras, refreshAi])
}
